import { throttle } from 'es-toolkit';
import { useEffect, useRef } from 'react';

import { numberFormatter } from '@flipster/utils/num';

import { ExportedData, IExecutionLineAdapter, VisibleTimeRange } from '../../lib/charting_library';
import { ChartStyles } from '../misc/constants';
import TradingView from '../trading-view';

interface Props {
  tv: TradingView | undefined;
}

export const SetupHighLowPrice = ({ tv }: Props) => {
  const helperRef = useRef<HighLowPriceHelper>(null);

  useEffect(() => {
    if (!tv) return;
    if (!helperRef.current) helperRef.current = new HighLowPriceHelper(tv);
    const helper = helperRef.current;
    helper.setup();
  }, [tv]);

  return null;
};

class HighLowPriceHelper {
  #symbol: string = 'dummy';
  #pricePrecision: number = 2;
  #shapes: Record<BoundTypes, IExecutionLineAdapter | null> = {
    high: null,
    low: null,
  };

  constructor(private readonly tv: TradingView) {}

  async setup() {
    await this.tv.ready();
    this.tv.activeChart?.onVisibleRangeChanged()?.subscribe(null, this.#throttledDraw);
    this.tv.widget?.subscribe('onTick', () => {
      if (!this.tv.activeChart) return;
      this.#throttledDraw(this.tv.activeChart.getVisibleRange());
    });
  }

  async #draw(range?: VisibleTimeRange) {
    try {
      if (!range || !this.tv.enabled || !this.tv.activeChart) return;
      if (this.#symbol !== this.tv.symbol) {
        this.#symbol = this.tv.symbol;
        this.#pricePrecision = this.tv.symbolPricePrecision;
        this.#shapes.high?.remove();
        this.#shapes.high = null;
        this.#shapes.low?.remove();
        this.#shapes.low = null;
      }
      const data = await this.tv.activeChart.exportData({
        ...range,
        includeUserTime: true,
        includeOHLCValuesForSingleValuePlots: true,
      });
      const highPrice = this.#findBoundPrice('high', data);
      const lowPrice = this.#findBoundPrice('low', data);
      this.#drawArrow('high', highPrice);
      this.#drawArrow('low', lowPrice);
    } catch {
      // Uncaught (in promise) Error: Assertion failed: Range must contain at least 1 time point
    }
  }

  #findIndexByPlotTitle(schema: ExportedData['schema'], plotTitle: string) {
    return schema.findIndex(
      (item) => item.type === 'value' && item.sourceType === 'series' && item.plotTitle === plotTitle,
    );
  }

  #findBoundPrice(bound: BoundTypes, data: ExportedData) {
    const records = data.data;
    const indexField = this.#findIndexByPlotTitle(data.schema, bound);
    if (indexField === -1) return null;

    const compare = (best: BoundPrice, curr: number) => (bound === 'high' ? curr > best.price : curr < best.price);
    return records.reduce<BoundPrice | null>((best, row, index) => {
      const time = row[1];
      const price = row[indexField];
      if (!best || compare(best, price)) {
        return { time, price, index };
      }
      return best;
    }, null);
  }

  #drawArrow(bound: BoundTypes, value: BoundPrice | null) {
    if (!this.tv.activeChart) return;
    if (!value) {
      this.#shapes[bound]?.remove();
      this.#shapes[bound] = null;
      return;
    }
    const boundInfo = HighLowMapper[bound];
    this.#shapes[bound] ??= this.tv
      .createExecutionShape()
      .setDirection(boundInfo.direction)
      .setTextColor(ChartStyles.Color.Text4)
      .setArrowColor(ChartStyles.Color.Text4)
      .setArrowSpacing(bound === 'low' ? 4 : 0); // low 는 기본적으로 space 가 더 적어 보임
    this.#shapes[bound]
      .setTime(value.time)
      .setPrice(value.price)
      .setText(
        boundInfo.prefix +
          numberFormatter(value.price, { method: 'round-half-up', toFixed: true, precision: this.#pricePrecision }),
      );
  }

  get #throttledDraw() {
    return throttle(this.#draw.bind(this), 500, { edges: ['leading'] });
  }
}

const HighLowMapper = {
  high: {
    prefix: 'High ',
    direction: 'sell' as const,
  },
  low: {
    prefix: 'Low ',
    direction: 'buy' as const,
  },
};

type BoundTypes = 'high' | 'low';

interface BoundPrice {
  time: number;
  price: number;
  index: number;
}
